import React, { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, X, ShieldAlert } from 'lucide-react';
import { disableNetwork, enableNetwork, db } from '../firebase';
import { toast } from 'sonner';

export const QuotaWarningBanner: React.FC = () => {
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    const handleCustomQuotaEvent = () => {
      setQuotaExceeded(true);
    };

    const handleGlobalError = (event: ErrorEvent) => {
      const message = event.message || '';
      if (message.includes('Quota exceeded') || message.includes('quota') || message.includes('Quota limit exceeded')) {
        setQuotaExceeded(true);
        disableNetwork(db).catch(() => {});
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason?.message || String(event.reason || '');
      if (reason.includes('Quota exceeded') || reason.includes('quota') || reason.includes('Quota limit exceeded')) {
        setQuotaExceeded(true);
        disableNetwork(db).catch(() => {});
      }
    };

    window.addEventListener('firestore-quota-exceeded', handleCustomQuotaEvent);
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('firestore-quota-exceeded', handleCustomQuotaEvent);
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  if (!quotaExceeded) return null;

  const handleSwitchToOffline = async () => {
    try {
      await disableNetwork(db);
      toast.success('Switched to local offline cache mode to bypass quota limits.');
      setQuotaExceeded(false);
    } catch (err) {
      console.error('Failed to disable network:', err);
    }
  };

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await enableNetwork(db);
      toast.success('Re-enabled cloud synchronization.');
      setQuotaExceeded(false);
    } catch (err) {
      toast.error('Quota limit still exceeded. Continuing in local cache mode.');
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-amber-600 text-white px-4 py-3 shadow-lg flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm font-medium">
      <div className="flex items-center gap-2.5">
        <ShieldAlert className="w-5 h-5 shrink-0 animate-pulse text-amber-200" />
        <span>
          <strong>Firestore Quota Exceeded:</strong> Cloud database request limits have been reached for today. The application is running in local cached mode.
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={handleSwitchToOffline}
          className="px-3 py-1.5 bg-amber-700 hover:bg-amber-800 text-white rounded-lg font-bold transition-colors shadow-sm"
        >
          Use Local Cache
        </button>
        <button
          onClick={handleRetry}
          disabled={isRetrying}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-amber-900 hover:bg-amber-50 rounded-lg font-bold transition-colors shadow-sm"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isRetrying ? 'animate-spin' : ''}`} />
          Retry Connection
        </button>
        <button
          onClick={() => setQuotaExceeded(false)}
          className="p-1 hover:bg-amber-700/50 rounded-lg transition-colors text-amber-200 hover:text-white"
          title="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
